/**
 * Meaningful Conversations Dashboard
 * Admin UI: manage cycles, upload CSVs, view analytics
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  HeartHandshake,
  ArrowLeft,
  Plus,
  Upload,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Users,
  CheckCircle,
  Clock,
  FileText,
  X,
  AlertCircle,
  RefreshCw,
  Search,
  Download,
  Sparkles,
  Brain,
  Lightbulb,
  TrendingUp
} from 'lucide-react';
import {
  getCycles,
  getCycleDetail,
  createCycle,
  uploadRecipients,
  getAnalytics,
  getCycleAnalytics,
  getByLeaderAnalytics,
  getLeaderDrilldown,
  getCycleInsights,
  generateCycleInsights,
  checkIsMCAdmin
} from '../../services/mcAPI';
import * as XLSX from 'xlsx';
import './ConversationsDashboard.css';

const ConversationsDashboard = () => {
  const navigate = useNavigate();
  const { user, getAccessToken } = useAuth();

  // Access control
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState('cycles');

  // Cycles state
  const [cycles, setCycles] = useState([]);
  const [loadingCycles, setLoadingCycles] = useState(true);
  const [expandedCycle, setExpandedCycle] = useState(null);
  const [cycleDetail, setCycleDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Create cycle state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCycleName, setNewCycleName] = useState('');
  const [newCycleDesc, setNewCycleDesc] = useState('');
  const [newCycleEndDate, setNewCycleEndDate] = useState('');
  const [creating, setCreating] = useState(false);

  // Upload state
  const [uploadCycleId, setUploadCycleId] = useState(null);
  const [csvText, setCsvText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [selectedAnalyticsCycle, setSelectedAnalyticsCycle] = useState(null);
  const [cycleAnalyticsData, setCycleAnalyticsData] = useState(null);
  // Per-leader analytics
  const [byLeaderData, setByLeaderData] = useState(null);
  const [expandedLeader, setExpandedLeader] = useState(null);
  const [leaderDrilldown, setLeaderDrilldown] = useState(null);
  const [loadingLeader, setLoadingLeader] = useState(false);
  const [leaderFilter, setLeaderFilter] = useState('');
  const [exporting, setExporting] = useState(false);

  // Error state
  const [error, setError] = useState(null);

  // AI Insights state
  const [cycleInsights, setCycleInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [generatingInsights, setGeneratingInsights] = useState(false);
  const [insightsPolling, setInsightsPolling] = useState(false);

  const loadCycles = useCallback(async () => {
    setLoadingCycles(true);
    setError(null);
    try {
      const data = await getCycles();
      setCycles(data.cycles || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingCycles(false);
    }
  }, []);

  useEffect(() => {
    const verifyAccess = async () => {
      try {
        const token = await getAccessToken();
        const result = await checkIsMCAdmin(token);
        setHasAccess(result?.isMCAdmin || false);
      } catch (err) {
        console.error('MC admin check failed:', err);
        setHasAccess(false);
      } finally {
        setAccessChecked(true);
      }
    };
    verifyAccess();
  }, [getAccessToken]);

  useEffect(() => {
    if (hasAccess) loadCycles();
  }, [loadCycles, hasAccess]);

  // Expand cycle to see recipients
  const toggleCycleDetail = async (cycleId) => {
    if (expandedCycle === cycleId) {
      setExpandedCycle(null);
      setCycleDetail(null);
      return;
    }
    setExpandedCycle(cycleId);
    setLoadingDetail(true);
    try {
      const data = await getCycleDetail(cycleId);
      setCycleDetail(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Create cycle
  const handleCreateCycle = async (e) => {
    e.preventDefault();
    if (!newCycleName.trim() || !newCycleEndDate) return;
    setCreating(true);
    setError(null);
    try {
      await createCycle(newCycleName.trim(), newCycleDesc.trim(), user?.email || 'admin', newCycleEndDate);
      setNewCycleName('');
      setNewCycleDesc('');
      setNewCycleEndDate('');
      setShowCreateModal(false);
      await loadCycles();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  // Upload CSV
  const handleUpload = async () => {
    if (!csvText.trim() || !uploadCycleId) return;
    setUploading(true);
    setUploadResult(null);
    setError(null);
    try {
      // Parse emails from CSV text — one per line, or comma-separated
      const emails = csvText
        .split(/[\n,]+/)
        .map(e => e.trim())
        .filter(e => e.length > 0 && e.includes('@'));
      if (emails.length === 0) {
        setError('No valid emails found in the input');
        setUploading(false);
        return;
      }
      const result = await uploadRecipients(uploadCycleId, emails);
      setUploadResult(result);
      setCsvText('');
      await loadCycles();
      // Refresh detail if this cycle is expanded
      if (expandedCycle === uploadCycleId) {
        const data = await getCycleDetail(uploadCycleId);
        setCycleDetail(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  // Handle file drop/select
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result);
    reader.readAsText(file);
  };

  // Load analytics
  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    setError(null);
    try {
      const data = await getAnalytics();
      setAnalytics(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const loadCycleAnalytics = async (cycleId) => {
    setSelectedAnalyticsCycle(cycleId);
    setLoadingAnalytics(true);
    setCycleInsights(null);
    // Reset per-leader state when switching cycles
    setByLeaderData(null);
    setExpandedLeader(null);
    setLeaderDrilldown(null);
    setLeaderFilter('');
    try {
      const data = await getCycleAnalytics(cycleId);
      setCycleAnalyticsData(data);
      // Load per-leader rollup (non-blocking; failure shouldn't break the view)
      getByLeaderAnalytics(cycleId)
        .then(setByLeaderData)
        .catch(err => console.error('by-leader load failed:', err.message));
      // Also load insights
      loadInsights(cycleId);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const toggleLeaderDrilldown = async (managerEmail) => {
    if (expandedLeader === managerEmail) {
      setExpandedLeader(null);
      setLeaderDrilldown(null);
      return;
    }
    setExpandedLeader(managerEmail);
    setLeaderDrilldown(null);
    setLoadingLeader(true);
    try {
      const data = await getLeaderDrilldown(selectedAnalyticsCycle, managerEmail);
      setLeaderDrilldown(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingLeader(false);
    }
  };

  /**
   * Export the current cycle's results to a multi-sheet Excel workbook, built
   * entirely client-side with SheetJS. Sheets:
   *   1. By Leader           — one row per leader (invited/responded/pending + rate)
   *   2. Individual Responses — one row per respondent (answer + feedback)
   *   3. All Recipients       — every invited recipient incl. non-responders + status
   */
  const handleExportExcel = async () => {
    if (!selectedAnalyticsCycle) return;
    setExporting(true);
    try {
      // Fetch the full roster (incl. non-responders) in one call.
      const full = await getByLeaderAnalytics(selectedAnalyticsCycle, 'full');
      const cycleName = full?.cycle_name || cycleAnalyticsData?.cycle?.name || `cycle-${selectedAnalyticsCycle}`;

      // Sheet 1: By Leader
      const leaderRows = (full?.leaders || []).map(L => ({
        Leader: L.manager_name,
        Email: L.manager_email || '',
        Invited: L.invited,
        Responded: L.responded,
        Pending: L.pending,
        'Response Rate (%)': L.response_rate,
        Yes: L.yes_count,
        No: L.no_count
      }));

      // Sheet 2: Individual Responses (responders only, from the cycle analytics)
      const respRows = (cycleAnalyticsData?.responses || []).map(r => ({
        Name: (r.first_name && r.last_name) ? `${r.first_name} ${r.last_name}` : r.recipient_email,
        Email: r.recipient_email,
        Department: r.department || '',
        Manager: r.manager_name || '',
        Answer: r.has_meaningful_conversation ? 'Yes' : 'No',
        Feedback: r.feedback_text || '',
        Date: r.responded_at ? new Date(r.responded_at).toLocaleDateString() : ''
      }));

      // Sheet 3: All Recipients (incl. non-responders)
      const allRows = (full?.all_recipients || []).map(r => ({
        Name: (r.first_name && r.last_name) ? `${r.first_name} ${r.last_name}` : r.recipient_email,
        Email: r.recipient_email,
        Department: r.department || '',
        Leader: r.manager_name || '',
        'Leader Email': r.manager_email || '',
        Status: r.status,
        Responded: r.responded ? 'Yes' : 'No',
        Answer: r.answer || '',
        Feedback: r.feedback_text || '',
        Date: r.responded_at ? new Date(r.responded_at).toLocaleDateString() : ''
      }));

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(leaderRows.length ? leaderRows : [{}]), 'By Leader');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(respRows.length ? respRows : [{}]), 'Individual Responses');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(allRows.length ? allRows : [{}]), 'All Recipients');

      const safe = cycleName.replace(/[\\/?*[\]:]/g, ' ').substring(0, 60).trim();
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `MC - ${safe} - ${stamp}.xlsx`);
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  const loadInsights = async (cycleId) => {
    setLoadingInsights(true);
    try {
      const data = await getCycleInsights(cycleId);
      setCycleInsights(data.insights);
      // If processing, start polling
      if (data.insights?.status === 'PROCESSING' || data.insights?.status === 'PENDING') {
        startInsightsPolling(cycleId);
      }
    } catch (err) {
      console.error('Failed to load insights:', err);
    } finally {
      setLoadingInsights(false);
    }
  };

  const startInsightsPolling = (cycleId) => {
    if (insightsPolling) return;
    setInsightsPolling(true);
    const interval = setInterval(async () => {
      try {
        const data = await getCycleInsights(cycleId);
        setCycleInsights(data.insights);
        if (data.insights?.status === 'COMPLETED' || data.insights?.status === 'FAILED') {
          clearInterval(interval);
          setInsightsPolling(false);
          setGeneratingInsights(false);
        }
      } catch {
        clearInterval(interval);
        setInsightsPolling(false);
      }
    }, 5000);
    // Cleanup after 5 minutes max
    setTimeout(() => { clearInterval(interval); setInsightsPolling(false); }, 300000);
  };

  const handleGenerateInsights = async (cycleId) => {
    setGeneratingInsights(true);
    try {
      await generateCycleInsights(cycleId);
      setCycleInsights({ status: 'PENDING' });
      startInsightsPolling(cycleId);
    } catch (err) {
      setError(err.message);
      setGeneratingInsights(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'analytics') {
      loadAnalytics();
    }
  }, [activeTab]);

  // ---- FILTERABLE TABLE COMPONENT ----
  const FilterableTable = ({ columns, data, tableKey, pageSize }) => {
    const [filters, setFilters] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const hasFilters = columns.some(col => col.filterable !== false);
    const isPaginated = !!pageSize;

    const filteredData = useMemo(() => {
      if (!data) return [];
      return data.filter(row =>
        columns.every(col => {
          if (col.filterable === false) return true;
          const filterVal = (filters[col.key] || '').toLowerCase();
          if (!filterVal) return true;
          const cellVal = String(col.render ? col.renderRaw?.(row) ?? col.render(row) : row[col.key] ?? '').toLowerCase();
          return cellVal.includes(filterVal);
        })
      );
    }, [data, filters, columns]);

    // Reset page when filters change
    useEffect(() => { setCurrentPage(1); }, [filters]);

    const totalPages = isPaginated ? Math.ceil(filteredData.length / pageSize) : 1;
    const paginatedData = isPaginated
      ? filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize)
      : filteredData;

    const updateFilter = (key, value) => {
      setFilters(prev => ({ ...prev, [key]: value }));
    };

    return (
      <div>
        <table className="mc-table">
          <thead>
            <tr>
              {columns.map(col => <th key={col.key}>{col.label}</th>)}
            </tr>
            {hasFilters && (
              <tr className="mc-filter-row">
                {columns.map(col => (
                  <th key={`filter-${col.key}`}>
                    {col.filterable !== false ? (
                      <div className="mc-filter-input-wrap">
                        <Search size={11} className="mc-filter-icon" />
                        <input
                          type="text"
                          className="mc-filter-input"
                          placeholder="Filter..."
                          value={filters[col.key] || ''}
                          onChange={e => updateFilter(col.key, e.target.value)}
                          aria-label={`Filter by ${col.label}`}
                        />
                      </div>
                    ) : null}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr><td colSpan={columns.length} style={{ textAlign: 'center', color: '#9ca3af', padding: '1rem' }}>No matching results</td></tr>
            ) : (
              paginatedData.map((row, i) => (
                <tr key={row._key || i}>
                  {columns.map(col => (
                    <td key={col.key}>{col.render ? col.render(row) : row[col.key]}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
        {isPaginated && totalPages > 1 && (
          <div className="mc-pagination">
            <span className="mc-pagination-info">
              Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, filteredData.length)} of {filteredData.length}
            </span>
            <div className="mc-pagination-controls">
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="mc-pagination-btn">«</button>
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="mc-pagination-btn">‹</button>
              <span className="mc-pagination-page">Page {currentPage} of {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="mc-pagination-btn">›</button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="mc-pagination-btn">»</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ---- COLUMN DEFINITIONS ----
  const pctBadge = (val) => {
    const n = parseFloat(val);
    const cls = n >= 70 ? 'mc-answer-yes' : n < 50 ? 'mc-answer-no' : '';
    return <span className={cls} style={{padding: '0.15rem 0.4rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600}}>{val}%</span>;
  };

  const deptColumns = [
    { key: 'department', label: 'Department' },
    { key: 'total', label: 'Total', filterable: false },
    { key: 'yes_count', label: 'Yes', filterable: false },
    { key: 'no_count', label: 'No', filterable: false, render: r => parseInt(r.total) - parseInt(r.yes_count), renderRaw: r => String(parseInt(r.total) - parseInt(r.yes_count)) },
    { key: 'yes_percentage', label: 'Yes %', filterable: false, render: r => pctBadge(r.yes_percentage), renderRaw: r => r.yes_percentage }
  ];

  const responseColumns = [
    { key: 'name', label: 'Name', render: r => r.first_name && r.last_name ? `${r.first_name} ${r.last_name}` : r.recipient_email, renderRaw: r => r.first_name && r.last_name ? `${r.first_name} ${r.last_name}` : r.recipient_email },
    { key: 'department', label: 'Department', render: r => r.department || '—' },
    { key: 'job_title', label: 'Job Title', render: r => r.job_title || '—' },
    { key: 'manager_name', label: 'Manager', render: r => r.manager_name || '—' },
    { key: 'answer', label: 'Answer', render: r => <span className={`mc-answer ${r.has_meaningful_conversation ? 'mc-answer-yes' : 'mc-answer-no'}`}>{r.has_meaningful_conversation ? 'Yes' : 'No'}</span>, renderRaw: r => r.has_meaningful_conversation ? 'Yes' : 'No' },
    { key: 'feedback_text', label: 'Feedback', render: r => <span className="mc-feedback-cell">{r.feedback_text || '—'}</span>, renderRaw: r => r.feedback_text || '' },
    { key: 'responded_at', label: 'Date', render: r => new Date(r.responded_at).toLocaleDateString(), renderRaw: r => new Date(r.responded_at).toLocaleDateString() }
  ];

  // ---- RENDER ----

  if (!accessChecked) {
    return (
      <div className="mc-dashboard" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '48px', height: '48px', border: '4px solid rgba(245,158,11,0.2)', borderTopColor: '#d97706', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }}></div>
          <p style={{ color: '#6b7280' }}>Checking access...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="mc-dashboard" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '2rem' }}>
          <AlertCircle size={48} color="#d97706" style={{ marginBottom: '1rem' }} />
          <h2 style={{ fontSize: '1.5rem', color: '#1f1f1f', marginBottom: '0.75rem' }}>Access Denied</h2>
          <p style={{ color: '#6b7280', marginBottom: '2rem' }}>You don't have admin access to Meaningful Conversations.</p>
          <button onClick={() => navigate('/')} className="mc-btn mc-btn-primary">Back to My Surveys</button>
        </div>
      </div>
    );
  }

  return (
    <div className="mc-dashboard">
      {/* Header */}
      <header className="mc-header">
        <div className="mc-header-inner">
          <div className="mc-header-left">
            <button onClick={() => navigate('/apps')} className="mc-back-btn" aria-label="Back to applications">
              <ArrowLeft size={20} />
            </button>
            <div className="mc-header-brand">
              <HeartHandshake size={28} color="#f59e0b" />
              <div>
                <h1 className="mc-header-title">Meaningful Conversations</h1>
                <p className="mc-header-subtitle">1:1 Conversation Survey Platform</p>
              </div>
            </div>
          </div>
          <div className="mc-header-right">
            <span className="mc-user-name">{user?.name || user?.email || ''}</span>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="mc-tabs">
        <button className={`mc-tab ${activeTab === 'cycles' ? 'mc-tab-active' : ''}`} onClick={() => setActiveTab('cycles')}>
          <FileText size={16} /> Survey Cycles
        </button>
        <button className={`mc-tab ${activeTab === 'analytics' ? 'mc-tab-active' : ''}`} onClick={() => setActiveTab('analytics')}>
          <BarChart3 size={16} /> Analytics
        </button>
      </div>

      <main className="mc-main">
        {/* Error banner */}
        {error && (
          <div className="mc-error">
            <AlertCircle size={16} />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="mc-error-close"><X size={14} /></button>
          </div>
        )}

        {/* ===== CYCLES TAB ===== */}
        {activeTab === 'cycles' && (
          <div>
            <div className="mc-section-header">
              <h2 className="mc-section-title">Survey Cycles</h2>
              <div className="mc-section-actions">
                <button onClick={loadCycles} className="mc-btn mc-btn-secondary" disabled={loadingCycles}>
                  <RefreshCw size={14} className={loadingCycles ? 'mc-spin' : ''} /> Refresh
                </button>
                <button onClick={() => setShowCreateModal(true)} className="mc-btn mc-btn-primary">
                  <Plus size={14} /> New Cycle
                </button>
              </div>
            </div>

            {loadingCycles ? (
              <div className="mc-loading">Loading cycles...</div>
            ) : cycles.length === 0 ? (
              <div className="mc-empty">
                <FileText size={48} color="#d1d5db" />
                <p>No survey cycles yet. Create one to get started.</p>
              </div>
            ) : (
              <div className="mc-cycle-list">
                {cycles.map(cycle => (
                  <div key={cycle.id} className="mc-cycle-card">
                    <div className="mc-cycle-row" onClick={() => toggleCycleDetail(cycle.id)} role="button" tabIndex={0} aria-expanded={expandedCycle === cycle.id}>
                      <div className="mc-cycle-info">
                        <h3 className="mc-cycle-name">{cycle.name}</h3>
                        {cycle.description && <p className="mc-cycle-desc">{cycle.description}</p>}
                        <div className="mc-cycle-meta">
                          <span className={`mc-status mc-status-${cycle.status.toLowerCase()}`}>{cycle.status}</span>
                          <span className="mc-meta-item"><Users size={13} /> {cycle.total_recipients || 0} recipients</span>
                          <span className="mc-meta-item"><CheckCircle size={13} /> {cycle.responded_count || cycle.completed_count || 0} responded</span>
                          <span className="mc-meta-item"><Clock size={13} /> {new Date(cycle.created_at).toLocaleDateString()}</span>
                          {cycle.end_date && (() => {
                            const d = new Date(cycle.end_date);
                            const display = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
                            const isPast = display < new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
                            return <span className="mc-meta-item" style={{ color: isPast ? '#dc2626' : '#6b7280' }}>📅 Ends {display.toLocaleDateString()}</span>;
                          })()}
                        </div>
                      </div>
                      <div className="mc-cycle-actions">
                        <button
                          onClick={(e) => { e.stopPropagation(); setUploadCycleId(cycle.id); setUploadResult(null); }}
                          className="mc-btn mc-btn-small mc-btn-secondary"
                          title="Upload recipients"
                        >
                          <Upload size={13} /> Upload CSV
                        </button>
                        {expandedCycle === cycle.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </div>
                    </div>

                    {/* Expanded detail */}
                    {expandedCycle === cycle.id && (
                      <div className="mc-cycle-detail">
                        {loadingDetail ? (
                          <div className="mc-loading">Loading recipients...</div>
                        ) : cycleDetail?.instances?.length > 0 ? (
                          <FilterableTable
                            columns={[
                              { key: 'recipient_email', label: 'Email' },
                              { key: 'status', label: 'Status', render: r => <span className={`mc-status mc-status-${r.status.toLowerCase()}`}>{r.status}</span>, renderRaw: r => r.status },
                              { key: 'sent_at', label: 'Sent', filterable: false, render: r => r.sent_at ? new Date(r.sent_at).toLocaleDateString() : '—' },
                              { key: 'completed_at', label: 'Completed', filterable: false, render: r => r.completed_at ? new Date(r.completed_at).toLocaleDateString() : '—' },
                              { key: 'expires_at', label: 'Expires', filterable: false, render: r => new Date(r.expires_at).toLocaleDateString() }
                            ]}
                            data={cycleDetail.instances}
                            tableKey="cycle-recipients"
                            pageSize={50}
                          />
                        ) : (
                          <p className="mc-empty-text">No recipients yet. Upload a CSV to add them.</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== ANALYTICS TAB ===== */}
        {activeTab === 'analytics' && (
          <div>
            <div className="mc-section-header">
              <h2 className="mc-section-title">Analytics</h2>
              <button onClick={loadAnalytics} className="mc-btn mc-btn-secondary" disabled={loadingAnalytics}>
                <RefreshCw size={14} className={loadingAnalytics ? 'mc-spin' : ''} /> Refresh
              </button>
            </div>

            {loadingAnalytics && !analytics ? (
              <div className="mc-loading">Loading analytics...</div>
            ) : analytics ? (
              <div>
                {/* Summary cards */}
                <div className="mc-analytics-grid">
                  <div className="mc-analytics-card">
                    <div className="mc-analytics-value">
                      {analytics.by_cycle?.reduce((sum, c) => sum + (parseInt(c.total_recipients) || 0), 0) || 0}
                    </div>
                    <div className="mc-analytics-label">Total Recipients</div>
                  </div>
                  <div className="mc-analytics-card">
                    <div className="mc-analytics-value">{analytics.summary.total_responses}</div>
                    <div className="mc-analytics-label">Completed Surveys</div>
                  </div>
                  <div className="mc-analytics-card mc-analytics-yes">
                    <div className="mc-analytics-value">{analytics.summary.yes_count}</div>
                    <div className="mc-analytics-label">Answered "Yes"</div>
                  </div>
                  <div className="mc-analytics-card mc-analytics-no">
                    <div className="mc-analytics-value">{analytics.summary.no_count}</div>
                    <div className="mc-analytics-label">Answered "No"</div>
                  </div>
                  <div className="mc-analytics-card mc-analytics-yes">
                    <div className="mc-analytics-value">{analytics.summary.yes_percentage}%</div>
                    <div className="mc-analytics-label">"Yes" Rate</div>
                  </div>
                </div>

                {/* By cycle */}
                {analytics.by_cycle?.length > 0 && (
                  <div className="mc-analytics-section">
                    <h3 className="mc-analytics-section-title">By Cycle</h3>
                    <table className="mc-table">
                      <thead>
                        <tr><th>Cycle</th><th>Status</th><th>Recipients</th><th>Responded</th><th>Rate</th><th></th></tr>
                      </thead>
                      <tbody>
                        {analytics.by_cycle.map(c => (
                          <tr key={c.id}>
                            <td>{c.name}</td>
                            <td><span className={`mc-status mc-status-${c.status.toLowerCase()}`}>{c.status}</span></td>
                            <td>{c.total_recipients}</td>
                            <td>{c.responded_count}</td>
                            <td>{c.response_rate || 0}%</td>
                            <td>
                              <button onClick={() => loadCycleAnalytics(c.id)} className="mc-btn mc-btn-small mc-btn-secondary">
                                Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Cycle detail analytics */}
                {cycleAnalyticsData && selectedAnalyticsCycle && (
                  <div className="mc-analytics-section">
                    <div className="mc-section-header">
                      <h3 className="mc-analytics-section-title">
                        Cycle Detail: {cycleAnalyticsData.cycle?.name}
                      </h3>
                      <button onClick={() => { setSelectedAnalyticsCycle(null); setCycleAnalyticsData(null); }} className="mc-btn mc-btn-small mc-btn-secondary">
                        <X size={13} /> Close
                      </button>
                    </div>
                    <div className="mc-analytics-grid" style={{ marginBottom: '1rem' }}>
                      <div className="mc-analytics-card">
                        <div className="mc-analytics-value">{cycleAnalyticsData.cycle?.total_recipients || 0}</div>
                        <div className="mc-analytics-label">Total Recipients</div>
                      </div>
                      <div className="mc-analytics-card">
                        <div className="mc-analytics-value">{cycleAnalyticsData.summary.total_responses}</div>
                        <div className="mc-analytics-label">Completed Surveys</div>
                      </div>
                      <div className="mc-analytics-card mc-analytics-yes">
                        <div className="mc-analytics-value">{cycleAnalyticsData.summary.yes_count}</div>
                        <div className="mc-analytics-label">Answered "Yes"</div>
                      </div>
                      <div className="mc-analytics-card mc-analytics-no">
                        <div className="mc-analytics-value">{cycleAnalyticsData.summary.no_count}</div>
                        <div className="mc-analytics-label">Answered "No"</div>
                      </div>
                      <div className="mc-analytics-card">
                        <div className="mc-analytics-value">{cycleAnalyticsData.summary.response_rate}%</div>
                        <div className="mc-analytics-label">Response Rate</div>
                      </div>
                    </div>

                    {/* ===== AI INSIGHTS SECTION ===== */}
                    <div className="mc-detail-card mc-ai-insights-section">
                      <div className="mc-section-header" style={{ marginBottom: '1rem' }}>
                        <h4 className="mc-analytics-section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Sparkles size={18} color="#8d1f26" /> AI Insights
                        </h4>
                        {cycleInsights?.status === 'COMPLETED' && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            {cycleInsights.generated_at && (
                              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                Last generated: {new Date(cycleInsights.generated_at).toLocaleString()}
                              </span>
                            )}
                            <button
                              onClick={() => handleGenerateInsights(selectedAnalyticsCycle)}
                              className="mc-btn mc-btn-small mc-btn-secondary"
                              disabled={generatingInsights}
                            >
                              <RefreshCw size={13} className={generatingInsights ? 'mc-spin' : ''} /> Regenerate
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Not started */}
                      {(!cycleInsights || cycleInsights.status === 'NOT_STARTED') && !loadingInsights && (
                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                          <Brain size={48} color="#d1d5db" style={{ marginBottom: '1rem' }} />
                          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>AI-powered analysis of survey responses and feedback</p>
                          <button
                            onClick={() => handleGenerateInsights(selectedAnalyticsCycle)}
                            className="mc-btn mc-btn-primary"
                            disabled={generatingInsights}
                          >
                            <Sparkles size={14} /> Generate AI Insights
                          </button>
                        </div>
                      )}

                      {/* Loading / Processing */}
                      {(loadingInsights || cycleInsights?.status === 'PENDING' || cycleInsights?.status === 'PROCESSING') && (
                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                          <div style={{ width: '48px', height: '48px', border: '4px solid rgba(141,31,38,0.15)', borderTopColor: '#8d1f26', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }}></div>
                          <p style={{ color: '#6b7280', fontWeight: 500 }}>
                            {cycleInsights?.status === 'PROCESSING' ? 'Analyzing responses with AI...' : 'Starting analysis...'}
                          </p>
                          <p style={{ color: '#9ca3af', fontSize: '0.8125rem' }}>This may take 5–10 minutes for large datasets (1000+ responses)</p>
                        </div>
                      )}

                      {/* Failed */}
                      {cycleInsights?.status === 'FAILED' && (
                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                          <AlertCircle size={48} color="#ef4444" style={{ marginBottom: '1rem' }} />
                          <p style={{ color: '#ef4444', marginBottom: '0.5rem' }}>Insights generation failed</p>
                          <p style={{ color: '#6b7280', fontSize: '0.8125rem', marginBottom: '1.5rem' }}>{cycleInsights.error_message}</p>
                          <button
                            onClick={() => handleGenerateInsights(selectedAnalyticsCycle)}
                            className="mc-btn mc-btn-primary"
                            disabled={generatingInsights}
                          >
                            <RefreshCw size={14} /> Retry
                          </button>
                        </div>
                      )}

                      {/* Completed — show insights */}
                      {cycleInsights?.status === 'COMPLETED' && (
                        <div className="mc-ai-results">
                          {/* Sentiment Overview */}
                          <div className="mc-ai-sentiment-row">
                            <div className="mc-ai-sentiment-card mc-ai-positive">
                              <div className="mc-ai-sentiment-value">{cycleInsights.sentiment?.positive || 0}%</div>
                              <div className="mc-ai-sentiment-label">Positive</div>
                            </div>
                            <div className="mc-ai-sentiment-card mc-ai-neutral">
                              <div className="mc-ai-sentiment-value">{cycleInsights.sentiment?.neutral || 0}%</div>
                              <div className="mc-ai-sentiment-label">Neutral</div>
                            </div>
                            <div className="mc-ai-sentiment-card mc-ai-negative">
                              <div className="mc-ai-sentiment-value">{cycleInsights.sentiment?.negative || 0}%</div>
                              <div className="mc-ai-sentiment-label">Negative</div>
                            </div>
                          </div>

                          {/* Emotion Categories */}
                          {cycleInsights.emotion_categories?.length > 0 && (
                            <div className="mc-ai-block">
                              <h5 className="mc-ai-block-title"><TrendingUp size={15} /> Emotion Categories</h5>
                              <div className="mc-ai-emotion-grid">
                                {cycleInsights.emotion_categories.map((cat, i) => (
                                  <div key={i} className="mc-ai-emotion-card">
                                    <div className="mc-ai-emotion-name">{cat.name}</div>
                                    <div className="mc-ai-emotion-count">{cat.count || 0} responses ({cat.percentage || 0}%)</div>
                                    <div className="mc-ai-emotion-desc">{cat.description}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Executive Summary */}
                          {cycleInsights.executive_summary && (
                            <div className="mc-ai-block">
                              <h5 className="mc-ai-block-title"><Brain size={15} /> Executive Summary</h5>
                              <div className="mc-ai-text-block">
                                {cycleInsights.executive_summary.split('\n').filter(p => p.trim()).map((p, i) => (
                                  <p key={i}>{p}</p>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Feedback Summary */}
                          {cycleInsights.feedback_summary && (
                            <div className="mc-ai-block">
                              <h5 className="mc-ai-block-title"><FileText size={15} /> Feedback Summary</h5>
                              <div className="mc-ai-text-block">
                                {cycleInsights.feedback_summary.split('\n').filter(p => p.trim()).map((p, i) => (
                                  <p key={i}>{p}</p>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Key Themes */}
                          {cycleInsights.key_themes?.length > 0 && (
                            <div className="mc-ai-block">
                              <h5 className="mc-ai-block-title"><Lightbulb size={15} /> Key Themes</h5>
                              <div className="mc-ai-themes">
                                {cycleInsights.key_themes.map((theme, i) => (
                                  <div key={i} className="mc-ai-theme-card">
                                    <div className="mc-ai-theme-name">{theme.theme}</div>
                                    <div className="mc-ai-theme-desc">{theme.description}</div>
                                    {theme.response_count > 0 && (
                                      <span className="mc-ai-theme-count">{theme.response_count} responses</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Recommendations */}
                          {cycleInsights.recommendations?.length > 0 && (
                            <div className="mc-ai-block">
                              <h5 className="mc-ai-block-title"><Sparkles size={15} /> Recommendations</h5>
                              <div className="mc-ai-recommendations">
                                {cycleInsights.recommendations.map((rec, i) => (
                                  <div key={i} className="mc-ai-rec-card">
                                    <div className="mc-ai-rec-header">
                                      <span className="mc-ai-rec-title">{rec.title}</span>
                                      <span className={`mc-ai-rec-priority mc-ai-priority-${(rec.priority || 'medium').toLowerCase()}`}>
                                        {rec.priority || 'MEDIUM'}
                                      </span>
                                    </div>
                                    <div className="mc-ai-rec-desc">{rec.description}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Meta */}
                          <div className="mc-ai-meta">
                            <span>{cycleInsights.total_responses_analyzed} responses analyzed ({cycleInsights.total_feedback_analyzed} with feedback)</span>
                            <span>Generated {cycleInsights.generated_at ? new Date(cycleInsights.generated_at).toLocaleString() : ''}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Cycle by department */}
                    {cycleAnalyticsData.by_department?.length > 0 && (
                      <div className="mc-detail-card">
                        <h4 className="mc-analytics-section-title">By Department</h4>
                        <FilterableTable columns={deptColumns} data={cycleAnalyticsData.by_department} tableKey="cycle-dept" pageSize={15} />
                      </div>
                    )}

                    {/* Cycle by leader — response rate (invited vs responded) with drill-down */}
                    {byLeaderData?.leaders?.length > 0 && (
                      <div className="mc-detail-card">
                        <div className="mc-section-header-row">
                          <h4 className="mc-analytics-section-title">By Leader (Response Rate)</h4>
                          <button
                            onClick={handleExportExcel}
                            className="mc-btn mc-btn-small mc-btn-secondary"
                            disabled={exporting}
                            title="Export leaders, individual responses, and all recipients to Excel"
                          >
                            <Download size={14} /> {exporting ? 'Exporting…' : 'Export to Excel'}
                          </button>
                        </div>
                        <div className="mc-leader-search">
                          <Search size={14} />
                          <input
                            type="text"
                            placeholder="Search leader by name or email…"
                            value={leaderFilter}
                            onChange={(e) => setLeaderFilter(e.target.value)}
                          />
                        </div>
                        <table className="mc-table">
                          <thead>
                            <tr>
                              <th>Leader</th><th>Invited</th><th>Responded</th><th>Pending</th><th>Response Rate</th><th>Yes</th><th>No</th><th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {byLeaderData.leaders
                              .filter((L) => {
                                const q = leaderFilter.trim().toLowerCase();
                                if (!q) return true;
                                return (L.manager_name || '').toLowerCase().includes(q) ||
                                       (L.manager_email || '').toLowerCase().includes(q);
                              })
                              .map((L) => (
                              <React.Fragment key={L.manager_email || '__unknown__'}>
                                <tr>
                                  <td>{L.manager_name}{L.manager_email ? <span className="mc-leader-email"> ({L.manager_email})</span> : ''}</td>
                                  <td>{L.invited}</td>
                                  <td>{L.responded}</td>
                                  <td>{L.pending}</td>
                                  <td>{pctBadge(L.response_rate)}</td>
                                  <td>{L.yes_count}</td>
                                  <td>{L.no_count}</td>
                                  <td>
                                    {L.manager_email && (
                                      <button
                                        onClick={() => toggleLeaderDrilldown(L.manager_email)}
                                        className="mc-btn mc-btn-small mc-btn-secondary"
                                      >
                                        {expandedLeader === L.manager_email ? 'Hide' : 'View reports'}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                                {expandedLeader === L.manager_email && (
                                  <tr>
                                    <td colSpan={8} style={{ background: '#fafafa', padding: '0.75rem 1rem' }}>
                                      {loadingLeader ? (
                                        <div className="mc-loading">Loading reports…</div>
                                      ) : leaderDrilldown?.reports?.length > 0 ? (
                                        <table className="mc-table mc-subtable">
                                          <thead>
                                            <tr><th>Report</th><th>Status</th><th>Responded</th><th>Answer</th><th>Feedback</th></tr>
                                          </thead>
                                          <tbody>
                                            {leaderDrilldown.reports.map((r) => (
                                              <tr key={r.recipient_email}>
                                                <td>{r.first_name && r.last_name ? `${r.first_name} ${r.last_name}` : r.recipient_email}</td>
                                                <td><span className={`mc-status mc-status-${(r.status || '').toLowerCase()}`}>{r.status}</span></td>
                                                <td>{r.responded_at ? new Date(r.responded_at).toLocaleDateString() : '—'}</td>
                                                <td>{r.answer ? <span className={`mc-answer ${r.answer === 'Yes' ? 'mc-answer-yes' : 'mc-answer-no'}`}>{r.answer}</span> : '—'}</td>
                                                <td><span className="mc-feedback-cell">{r.feedback_text || '—'}</span></td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      ) : (
                                        <p className="mc-empty-text">No reports found for this leader in this cycle.</p>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Individual responses */}
                    {cycleAnalyticsData.responses?.length > 0 && (
                      <div className="mc-detail-card">
                        <h4 className="mc-analytics-section-title">Individual Responses</h4>
                        <FilterableTable columns={responseColumns} data={cycleAnalyticsData.responses} tableKey="cycle-responses" pageSize={50} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="mc-empty">
                <BarChart3 size={48} color="#d1d5db" />
                <p>No analytics data available yet.</p>
              </div>
            )}
          </div>
        )}

        {/* ===== CREATE CYCLE MODAL ===== */}
        {showCreateModal && (
          <div className="mc-modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="mc-modal" onClick={e => e.stopPropagation()}>
              <div className="mc-modal-header">
                <h3>Create New Survey Cycle</h3>
                <button onClick={() => setShowCreateModal(false)} className="mc-modal-close"><X size={18} /></button>
              </div>
              <form onSubmit={handleCreateCycle}>
                <div className="mc-form-group">
                  <label htmlFor="cycle-name">Cycle Name *</label>
                  <input
                    id="cycle-name"
                    type="text"
                    value={newCycleName}
                    onChange={e => setNewCycleName(e.target.value)}
                    placeholder="e.g. Q1 2026 Meaningful Conversations"
                    required
                    autoFocus
                  />
                </div>
                <div className="mc-form-group">
                  <label htmlFor="cycle-desc">Description</label>
                  <textarea
                    id="cycle-desc"
                    value={newCycleDesc}
                    onChange={e => setNewCycleDesc(e.target.value)}
                    placeholder="Optional description for this cycle"
                    rows={3}
                  />
                </div>
                <div className="mc-form-group">
                  <label htmlFor="cycle-end-date">End Date *</label>
                  <input
                    id="cycle-end-date"
                    type="date"
                    value={newCycleEndDate}
                    onChange={e => setNewCycleEndDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                  <span className="mc-form-hint">Surveys will expire and the cycle will close after this date</span>
                </div>
                <div className="mc-modal-actions">
                  <button type="button" onClick={() => setShowCreateModal(false)} className="mc-btn mc-btn-secondary">Cancel</button>
                  <button type="submit" className="mc-btn mc-btn-primary" disabled={creating || !newCycleName.trim() || !newCycleEndDate}>
                    {creating ? 'Creating...' : 'Create Cycle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ===== UPLOAD CSV MODAL ===== */}
        {uploadCycleId && (
          <div className="mc-modal-overlay" onClick={() => { setUploadCycleId(null); setUploadResult(null); }}>
            <div className="mc-modal" onClick={e => e.stopPropagation()}>
              <div className="mc-modal-header">
                <h3>Upload Recipients</h3>
                <button onClick={() => { setUploadCycleId(null); setUploadResult(null); }} className="mc-modal-close"><X size={18} /></button>
              </div>

              {uploadResult ? (
                <div className="mc-upload-result">
                  <CheckCircle size={32} color="#10b981" />
                  <p className="mc-upload-result-text">{uploadResult.message}</p>
                  {uploadResult.duplicates > 0 && <p className="mc-upload-result-note">{uploadResult.duplicates} duplicate(s) skipped</p>}
                  {uploadResult.invalidEmails?.length > 0 && (
                    <p className="mc-upload-result-note">{uploadResult.invalidEmails.length} invalid email(s) skipped</p>
                  )}
                  <button onClick={() => { setUploadCycleId(null); setUploadResult(null); }} className="mc-btn mc-btn-primary">Done</button>
                </div>
              ) : (
                <div>
                  <div className="mc-form-group">
                    <label htmlFor="csv-file">Upload CSV file</label>
                    <input id="csv-file" type="file" accept=".csv,.txt" onChange={handleFileSelect} />
                  </div>
                  <div className="mc-form-group">
                    <label htmlFor="csv-text">Or paste emails (one per line or comma-separated)</label>
                    <textarea
                      id="csv-text"
                      value={csvText}
                      onChange={e => setCsvText(e.target.value)}
                      placeholder={"john.doe@ejgallo.com\njane.smith@ejgallo.com"}
                      rows={6}
                    />
                  </div>
                  <div className="mc-modal-actions">
                    <button type="button" onClick={() => { setUploadCycleId(null); setUploadResult(null); }} className="mc-btn mc-btn-secondary">Cancel</button>
                    <button onClick={handleUpload} className="mc-btn mc-btn-primary" disabled={uploading || !csvText.trim()}>
                      {uploading ? 'Uploading...' : 'Upload Recipients'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ConversationsDashboard;
